import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { dedupeTabsById } from '../utils/layoutTabUtils';
import { isAppTarget } from '../utils/target-scope.utils';
import type {
  LayoutState,
  MainContentTab,
  SidebarTab,
  Tab,
  TabGroup,
  TabNavigationState,
  TargetTabWorkspace,
} from './layoutStoreTypes';
import {
  dedupeApplicationTabs,
  filterTabsForTargetType,
  mergeTabsByOrder,
  omitNavigationForTabIds,
  partitionTabGroupsForAppTabs,
  pickNavigationForTabIds,
  resolveActiveTabIdAfterTargetSwitch,
  resolveSidebarTabForActiveMainContent,
} from './layoutTargetWorkspace.utils';

type ParkTargetTabWorkspaceParams = {
  mainContentTabs: Tab[];
  tabNavigationByTabId: Record<string, TabNavigationState>;
  tabHistory: string[];
  activeMainContentTabId: string | undefined;
  tabGroups: TabGroup[];
  applicationSpecificTabTypes: readonly MainContentTab[];
};

type ParkTargetTabWorkspaceResult = {
  workspace: TargetTabWorkspace;
  remainingTabs: Tab[];
  remainingNavigation: Record<string, TabNavigationState>;
  remainingHistory: string[];
  remainingGroups: TabGroup[];
  parkedAppTabIds: Set<string>;
};

type BuildRestoredTargetWorkspaceParams = {
  savedWorkspace: TargetTabWorkspace | undefined;
  nextTargetType: TargetTypeType | undefined;
  remainingTabs: Tab[];
  remainingNavigation: Record<string, TabNavigationState>;
  remainingHistory: string[];
  remainingGroups: TabGroup[];
};

type BuildRestoredTargetWorkspaceResult = {
  restoredAppTabs: Tab[];
  finalTabs: Tab[];
  tabNavigationByTabId: Record<string, TabNavigationState>;
  tabHistory: string[];
  tabGroups: TabGroup[];
  preferredActiveTabId: string | undefined;
};

type CloseTargetSpecificTabsOptions = {
  currentTargetId?: string;
  currentTargetType?: TargetTypeType;
  nextTargetId?: string;
  nextTargetType?: TargetTypeType;
};

type ApplyCloseTargetSpecificTabsParams = {
  state: Pick<
    LayoutState,
    | 'mainContentTabs'
    | 'activeMainContentTabId'
    | 'tabHistory'
    | 'tabNavigationByTabId'
    | 'tabGroups'
    | 'savedPinnedTabs'
    | 'tabSections'
  >;
  options?: CloseTargetSpecificTabsOptions;
  applicationSpecificTabTypes: readonly MainContentTab[];
  getSidebarTabForMainContentTab: (
    tabType: MainContentTab,
  ) => SidebarTab | null;
};

export function createEmptyTargetTabWorkspace(): TargetTabWorkspace {
  return {
    tabs: [],
    tabNavigationByTabId: {},
    tabHistory: [],
    activeMainContentTabId: undefined,
    tabGroups: [],
    tabOrder: [],
  };
}

export function createTargetTabWorkspaceFromTabs({
  tabs,
}: {
  tabs: Tab[];
}): TargetTabWorkspace {
  const dedupedTabs = dedupeTabsById({ tabs });

  return {
    tabs: dedupedTabs.map((tab) => ({ ...tab, isActive: false })),
    tabNavigationByTabId: {},
    tabHistory: [],
    activeMainContentTabId: undefined,
    tabGroups: [],
    tabOrder: dedupedTabs.map((tab) => tab.id),
  };
}

export function parkTargetTabWorkspace({
  mainContentTabs,
  tabNavigationByTabId,
  tabHistory,
  activeMainContentTabId,
  tabGroups,
  applicationSpecificTabTypes,
}: ParkTargetTabWorkspaceParams): ParkTargetTabWorkspaceResult {
  const applicationWorkspaceTabs = dedupeApplicationTabs({
    tabs: mainContentTabs,
    applicationSpecificTabTypes,
  });
  const parkedAppTabIds = new Set(
    applicationWorkspaceTabs.map((tab) => tab.id),
  );
  const { parkedGroups, remainingGroups } = partitionTabGroupsForAppTabs({
    tabGroups,
    appTabIds: parkedAppTabIds,
  });

  const workspace: TargetTabWorkspace = {
    tabs: applicationWorkspaceTabs.map((tab) => ({
      ...tab,
      isActive: false,
    })),
    tabNavigationByTabId: pickNavigationForTabIds({
      tabNavigationByTabId,
      tabIds: parkedAppTabIds,
    }),
    tabHistory: tabHistory.filter((tabId) => parkedAppTabIds.has(tabId)),
    activeMainContentTabId:
      activeMainContentTabId && parkedAppTabIds.has(activeMainContentTabId)
        ? activeMainContentTabId
        : undefined,
    tabGroups: parkedGroups,
    tabOrder: mainContentTabs.map((tab) => tab.id),
  };

  const remainingTabs = mainContentTabs.filter(
    (tab) => !applicationSpecificTabTypes.includes(tab.type),
  );
  const remainingTabIds = new Set(remainingTabs.map((tab) => tab.id));

  return {
    workspace,
    remainingTabs,
    remainingNavigation: omitNavigationForTabIds({
      tabNavigationByTabId,
      tabIds: parkedAppTabIds,
    }),
    remainingHistory: tabHistory.filter((tabId) => remainingTabIds.has(tabId)),
    remainingGroups,
    parkedAppTabIds,
  };
}

export function buildRestoredTargetWorkspace({
  savedWorkspace,
  nextTargetType,
  remainingTabs,
  remainingNavigation,
  remainingHistory,
  remainingGroups,
}: BuildRestoredTargetWorkspaceParams): BuildRestoredTargetWorkspaceResult {
  const defaultApplicationTab: Tab = {
    id: 'application-overview',
    title:
      nextTargetType && isAppTarget(nextTargetType)
        ? 'Home'
        : 'Target Overview',
    type: 'application',
    isActive: false,
  };

  const tabsToRestore =
    savedWorkspace && nextTargetType
      ? dedupeTabsById({
          tabs: filterTabsForTargetType({
            tabs: savedWorkspace.tabs,
            targetType: nextTargetType,
          }),
        })
      : savedWorkspace
        ? dedupeTabsById({ tabs: savedWorkspace.tabs })
        : [];

  const usedDefaultApplicationTab =
    tabsToRestore.length === 0 && !!nextTargetType;
  const effectiveTabsToRestore = usedDefaultApplicationTab
    ? [defaultApplicationTab]
    : tabsToRestore;

  const restoredAppTabs = effectiveTabsToRestore.map((tab) => ({
    ...tab,
    isActive: false,
  }));
  const restoredAppTabIds = new Set(restoredAppTabs.map((tab) => tab.id));

  const restoredNavigation = pickNavigationForTabIds({
    tabNavigationByTabId: savedWorkspace?.tabNavigationByTabId ?? {},
    tabIds: restoredAppTabIds,
  });

  const baseTabOrder =
    savedWorkspace?.tabOrder.length && savedWorkspace.tabOrder.length > 0
      ? savedWorkspace.tabOrder
      : [
          ...restoredAppTabs.map((tab) => tab.id),
          ...remainingTabs.map((tab) => tab.id),
        ];
  const baseTabOrderIds = new Set(baseTabOrder);
  const missingRestoredTabIds = restoredAppTabs
    .map((tab) => tab.id)
    .filter((tabId) => !baseTabOrderIds.has(tabId));
  const tabOrder = [...missingRestoredTabIds, ...baseTabOrder];

  const finalTabs = mergeTabsByOrder({
    restoredAppTabs,
    globalTabs: remainingTabs,
    tabOrder,
  });
  const finalTabIds = new Set(finalTabs.map((tab) => tab.id));

  const restoredHistory = (savedWorkspace?.tabHistory ?? []).filter((tabId) =>
    restoredAppTabIds.has(tabId),
  );

  const restoredGroups = (savedWorkspace?.tabGroups ?? [])
    .map((group) => ({
      ...group,
      tabIds: group.tabIds.filter((tabId) => restoredAppTabIds.has(tabId)),
    }))
    .filter((group) => group.tabIds.length > 0);

  const remainingGroupIds = new Set(remainingGroups.map((group) => group.id));
  const restoredGroupsById = new Map(
    restoredGroups.map((group) => [group.id, group]),
  );
  const mergedGroups = [
    ...remainingGroups.map((group) => {
      const restoredGroup = restoredGroupsById.get(group.id);

      if (!restoredGroup) {
        return group;
      }

      const remainingTabIds = new Set(group.tabIds);

      return {
        ...group,
        tabIds: [
          ...group.tabIds,
          ...restoredGroup.tabIds.filter(
            (tabId) => !remainingTabIds.has(tabId),
          ),
        ],
      };
    }),
    ...restoredGroups.filter((group) => !remainingGroupIds.has(group.id)),
  ];

  return {
    restoredAppTabs,
    finalTabs,
    tabNavigationByTabId: {
      ...remainingNavigation,
      ...restoredNavigation,
    },
    tabHistory: [
      ...restoredHistory,
      ...remainingHistory.filter((tabId) => finalTabIds.has(tabId)),
    ],
    tabGroups: mergedGroups,
    preferredActiveTabId:
      savedWorkspace?.activeMainContentTabId &&
      restoredAppTabIds.has(savedWorkspace.activeMainContentTabId)
        ? savedWorkspace.activeMainContentTabId
        : usedDefaultApplicationTab
          ? defaultApplicationTab.id
          : undefined,
  };
}

export function applyCloseTargetSpecificTabs({
  state,
  options,
  applicationSpecificTabTypes,
  getSidebarTabForMainContentTab,
}: ApplyCloseTargetSpecificTabsParams): Partial<LayoutState> | null {
  const {
    mainContentTabs,
    activeMainContentTabId,
    tabHistory,
    tabNavigationByTabId,
    tabGroups,
    savedPinnedTabs,
  } = state;
  const { currentTargetId, currentTargetType, nextTargetId, nextTargetType } =
    options ?? {};

  const currentTargetScopeKey =
    currentTargetId && currentTargetType
      ? `${currentTargetType}:${currentTargetId}`
      : undefined;
  const nextTargetScopeKey =
    nextTargetId && nextTargetType
      ? `${nextTargetType}:${nextTargetId}`
      : undefined;

  const {
    workspace: parkedWorkspace,
    remainingTabs,
    remainingNavigation,
    remainingHistory,
    remainingGroups,
  } = parkTargetTabWorkspace({
    mainContentTabs,
    tabNavigationByTabId,
    tabHistory,
    activeMainContentTabId,
    tabGroups,
    applicationSpecificTabTypes,
  });

  const updatedSavedPinnedTabs = currentTargetScopeKey
    ? {
        ...savedPinnedTabs,
        [currentTargetScopeKey]: parkedWorkspace,
      }
    : savedPinnedTabs;

  const savedNextWorkspace =
    nextTargetScopeKey && nextTargetType
      ? updatedSavedPinnedTabs[nextTargetScopeKey]
      : undefined;

  const {
    restoredAppTabs,
    finalTabs,
    tabNavigationByTabId: nextNavigation,
    tabHistory: nextHistory,
    tabGroups: nextGroups,
    preferredActiveTabId,
  } = buildRestoredTargetWorkspace({
    savedWorkspace:
      nextTargetScopeKey || nextTargetType
        ? (savedNextWorkspace ?? createEmptyTargetTabWorkspace())
        : undefined,
    nextTargetType,
    remainingTabs,
    remainingNavigation,
    remainingHistory,
    remainingGroups,
  });

  const shouldUpdate =
    finalTabs.length !== mainContentTabs.length ||
    restoredAppTabs.length > 0 ||
    Boolean(currentTargetScopeKey);

  if (!shouldUpdate) {
    return null;
  }

  const finalTabIds = new Set(finalTabs.map((tab) => tab.id));
  const currentActiveTab = mainContentTabs.find((tab) => tab.isActive);
  const wasActiveTabClosed = Boolean(
    currentActiveTab &&
      applicationSpecificTabTypes.includes(currentActiveTab.type),
  );

  const newActiveTabId = resolveActiveTabIdAfterTargetSwitch({
    finalTabIds,
    preferredActiveTabId,
    tabHistory: nextHistory,
    restoredAppTabs,
    finalTabs,
    wasActiveTabClosed,
  });

  const nextMainContentTabs = finalTabs.map((tab) => ({
    ...tab,
    isActive: tab.id === newActiveTabId,
  }));

  const nextSidebarTab = resolveSidebarTabForActiveMainContent({
    tabs: nextMainContentTabs,
    activeMainContentTabId: newActiveTabId,
    tabNavigationByTabId: nextNavigation,
    getSidebarTabForMainContentTab,
  });

  return {
    mainContentTabs: nextMainContentTabs,
    activeMainContentTabId: finalTabs.length === 0 ? undefined : newActiveTabId,
    tabHistory: nextHistory,
    tabNavigationByTabId: nextNavigation,
    tabGroups: nextGroups,
    savedPinnedTabs: updatedSavedPinnedTabs,
    ...(nextSidebarTab ? { selectedSidebarTab: nextSidebarTab } : {}),
  };
}
